import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import { alpha } from "@mui/material/styles";
import variables from "theme/styles/_variables.module.scss";

type ChartBarsSkeletonProps = {
  minHeight?: number;
  bars?: number; // deprecated, prefer groups
  groups?: number;
};

const bounceA = keyframes`
  0% { height: 30px; }
  50% { height: 140px; }
  100% { height: 60px; }
`;

const bounceB = keyframes`
  0% { height: 20px; }
  50% { height: 160px; }
  100% { height: 45px; }
`;

const ChartBarsSkeleton: React.FC<ChartBarsSkeletonProps> = (props) => {
  const minHeight = props.minHeight ?? 200;
  const groups = props.groups ?? 7;

  return (
    <Box sx={{ position: "relative", width: "100%", height: minHeight, minHeight, px: 2, py: 1 }}>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: (theme) =>
            `repeating-linear-gradient(to bottom, ${alpha(theme.palette.text.primary, 0.08)} 0, ${alpha(
              theme.palette.text.primary,
              0.08
            )} 1px, transparent 1px, transparent 48px)`,
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "flex-end",
          gap: 1.5,
        }}
      >
        {Array.from({ length: groups }).map((_, groupIdx) => (
          <Box
            key={groupIdx}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: 0.25,
              position: "relative",
              height: "100%",
            }}
          >
            <Box
              sx={{
                width: "clamp(10px, 28%, 22px)",
                height: 80,
                backgroundColor: variables.skeletonPaper,
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                boxShadow: (theme) => `0 1px 1px ${alpha(theme.palette.common.black, 0.08)}`,
                animation: `${bounceA} 2s ease-in-out ${groupIdx * 0.12}s infinite`,
                willChange: "height",
              }}
            />
            <Box
              sx={{
                width: "clamp(10px, 28%, 22px)",
                height: 100,
                backgroundColor: variables.skeletonPaper,
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                boxShadow: (theme) => `0 1px 1px ${alpha(theme.palette.common.black, 0.12)}`,
                animation: `${bounceB} 2s ease-in-out ${groupIdx * 0.12 + 0.3}s infinite`,
                willChange: "height",
              }}
            />
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          justifyContent: "center",
          mt: 2,
          px: 2,
        }}
      >
        {[1, 2, 3].map((idx) => (
          <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: variables.skeletonPaper,
              }}
            />
            <Box
              sx={{
                width: 60,
                height: 10,
                borderRadius: 1,
                backgroundColor: variables.skeletonPaper,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ChartBarsSkeleton;
